<template>
  <div class="min-h-screen bg-gray-50 flex flex-col pb-6 md:flex-row md:items-start md:justify-center">
    <!-- Main content -->
    <main class="flex-1 flex flex-col md:max-w-6xl md:mx-auto md:w-full">
      <!-- Header -->
      <div class="flex items-center justify-between px-5 pt-6">
        <div class="flex items-center gap-3" @click="goToProfile" style="cursor: pointer;">
          <img
            :src="(profile && profile.avatar) ? profile.avatar : 'https://api.castingup.it/media/users/placeholder/avatar_placeholder_app.png'"
            alt="Avatar"
            class="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <div class="text-xs text-left text-gray-500">Ben tornato</div>
            <div
              v-if="profile"
              class="font-bold text-base flex items-center gap-1 cursor-pointer"
              title="Vai al profilo"
            >
              {{ (profile && profile.nome && profile.cognome) ? profile.nome + ' ' + profile.cognome : (profile && profile.nome) ? profile.nome : 'Ospite' }} <span class="text-base">🎬</span>
            </div>
            <div
              v-else
              class="font-bold text-base flex items-center gap-1"
            >
              Ospite <span class="text-base">🎬</span>
            </div>
          </div>
        </div>
        <template v-if="profile">
          <button class="relative group" @click="showLogoutSheet = true" title="Logout">
            <i class="bx bx-log-out text-2xl text-gray-500 group-hover:text-red-500 transition"></i>
          </button>
        </template>
      </div>
      <!-- Profile completion card -->
      <div class="px-5 mt-6" v-if="profile">
        <ProfileCompletionWidget
          :title="'Completamento profilo'"
          :images="profileImages"
          :videos="profileVideos"
          :goToProfile="goToProfile"
        />
      </div>
      <!-- Desktop: Show detail below profile if subroute is active, else show casting lists -->
      <template v-if="isDesktopDetailRoute">
        <div class="px-5 mt-6">
          <router-view />
        </div>
      </template>
      <template v-else>
        <!-- Recent Castings -->
        <template v-if="castings.length > 0">
          <div class="flex items-center flex-start px-5 mt-8 mb-2">
            <div class="font-bold text-base">Casting per me</div>
          </div>
          <div class="relative px-5 pb-5">
            <!-- Arrow left -->
            <button
              v-if="isDesktop"
              @click="scrollCastings('left')"
              class="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center shadow transition hidden md:block"
              style="transform: translateY(-50%);"
              aria-label="Scroll left"
            >
              <i class="bx bx-chevron-left text-2xl"></i>
            </button>
            <!-- Castings row -->
            <div
              ref="castingsRow"
              class="flex gap-4 md:flex-row overflow-x-auto scroll-smooth"
              style="scrollbar-width: thin;"
            >
              <div v-for="casting in castings" :key="casting.id" class="flex flex-col">
                <CuCastingThumb
                  :image="casting.image"
                  :title="casting.title"
                  :bg="casting.bg"
                  :casting-id="casting.codice"
                  class="cursor-pointer h-48 rounded-2xl shadow-lg"
                  style="width: 270px;"
                  text-class="text-base md:text-2xl font-bold"
                  @click.native="goToCasting(casting.id)"
                  :subscriptionStatus="casting.stato_iscrizione"
                  :showSpecsSummary="false"
                  :pay="casting.pay"
                  :date="casting.date"
                  :place="casting.place"
                />
              </div>
            </div>
            <!-- Arrow right -->
            <button
              v-if="isDesktop"
              @click="scrollCastings('right')"
              class="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 border border-gray-200 rounded-full w-10 h-10 flex items-center justify-center shadow transition hidden md:block"
              style="transform: translateY(-50%);"
              aria-label="Scroll right"
            >
              <i class="bx bx-chevron-right text-2xl"></i>
            </button>
          </div>
        </template>
        <!-- Casting Recenti -->
        <div class="flex items-center justify-between px-5 mt-8 mb-2">
          <div class="font-bold text-base">Casting recenti</div>
        </div>
        <div class="flex flex-col gap-4 px-5 pb-5 md:grid md:grid-cols-3 md:gap-8">
          <div v-for="casting in recentCastings" :key="casting.id" class="flex flex-col">
            <CuCastingThumb
              :image="casting.image"
              :title="casting.title"
              :bg="casting.bg"
              :casting-id="casting.codice"
              class="cursor-pointer h-48 md:h-80 rounded-2xl shadow-lg w-full md:w-auto"
              text-class="text-base md:text-2xl font-bold"
              @click.native="goToCasting(casting.id)"
              :subscriptionStatus="casting.stato_iscrizione"
              :showSpecsSummary="isDesktop"
              :pay="casting.pay"
              :date="casting.date"
              :place="casting.place"
            />
          </div>
        </div>
      </template>
      <footer class="w-full text-center text-xs text-gray-400 py-4 mt-12">
        © {{ (new Date()).getFullYear() }} All In One Group Sagl. All Rights Reserved.
        <br/>
        <a href="https://castingup.it/informativa-privacy-castingup.pdf" target="_blank" rel="noopener" class="underline hover:text-gray-600">Privacy Policy</a>
        &middot;
        <a href="https://www.castingup.it/termini-e-condizioni-castingup.pdf" target="_blank" rel="noopener" class="underline hover:text-gray-600">Terms of Service</a>
      </footer>
    </main>

    <!-- Bottom nav
    <div class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex justify-around items-center h-16 z-50">
      <button class="flex flex-col items-center text-primary">
        <i class="bx bxs-home text-2xl"></i>
      </button>
      <button class="flex flex-col items-center text-gray-400">
        <i class="bx bx-search text-2xl"></i>
      </button>
      <button class="flex flex-col items-center text-gray-400">
        <i class="bx bx-bar-chart text-2xl"></i>
      </button>
      <button class="flex flex-col items-center text-gray-400">
        <i class="bx bx-user text-2xl"></i>
      </button>
    </div>-->

    <LogoutConfirmSheet
      :show="showLogoutSheet"
      @close="showLogoutSheet = false"
      @confirm="logout"
    />
    
  <!-- Loading Spinner Overlay -->
  <div v-if="isLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
    <i class="bx bx-loader-alt text-6xl text-primary animate-spin"></i>
  </div>
</div>
</template>

<script>
import appClient from '../api/appClient.js'
import CuCastingThumb from '../components/cu/CuCastingThumb.vue'
import LogoutConfirmSheet from '../components/dialog-sheets/LogoutConfirmSheet.vue'
import ProfileCompletionWidget from '../components/ProfileCompletionWidget.vue'
import eventBus from '../utils/eventBus.js'
export default {
  name: "OverviewView",
  components: { CuCastingThumb, LogoutConfirmSheet, ProfileCompletionWidget },
  data() {
    return {
      castings: [],
      recentCastings: [],
      showCastingSheet: false,
      selectedCastingId: null,
      isDesktop: false,
      profile: null,
      showLogoutSheet: false,
      isLoading: false, // Loading state for API requests
      profileImages: [],
      profileVideos: []
    }
  },
  computed: {
    isDesktopDetailRoute() {
      return (
        this.isDesktop &&
        this.$route.name === 'CastingDetailSheet'
      );
    }
  },
  async mounted() {
    this.checkDesktop()
    window.addEventListener('resize', this.checkDesktop)
    eventBus.on('refreshProfileAndMedia', this.fetchProfileAndMedia)
    this.isLoading = true;
    await Promise.all([
      this.fetchCastingsForMe(),
      this.fetchRecentCastings(),
      this.fetchProfileAndMedia()
    ]);
    this.isLoading = false;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkDesktop)
    eventBus.off('refreshProfileAndMedia', this.fetchProfileAndMedia)
  },
  methods: {
    async fetchProfileAndMedia() {
      // Fetch user profile and then images/videos
      const token = localStorage.getItem('castingup_app_token');
      if (!token) {
        this.profile = null;
        this.profileImages = [];
        this.profileVideos = [];
        return;
      }
      try {
        // Profile
        const profileRes = await appClient.post('/profile', {});
        if (profileRes.data && profileRes.data.status && profileRes.data.data && profileRes.data.data.id) {
          this.profile = profileRes.data.data;
        } else {
          this.profile = null;
        }
        // Images
        const imagesRes = await appClient.post('/media/mymedia', { type: 1 });
        if (imagesRes.data && imagesRes.data.status && Array.isArray(imagesRes.data.data)) {
          this.profileImages = imagesRes.data.data;
        } else {
          this.profileImages = [];
        }
        // Videos
        const videosRes = await appClient.post('/media/mymedia', { type: 2 });
        if (videosRes.data && videosRes.data.status && Array.isArray(videosRes.data.data)) {
          this.profileVideos = videosRes.data.data;
        } else {
          this.profileVideos = [];
        }
      } catch (e) {
        this.profile = null;
        this.profileImages = [];
        this.profileVideos = [];
      }
    },
    async fetchCastingsForMe() {
      try {
        const res = await appClient.post('/web/casting/list', { tipo_lista: 2 })
        if (res.data && res.data.status && Array.isArray(res.data.data)) {
          this.castings = res.data.data.map(c => ({
            id: c.id,
            codice: c.codice,
            image: c.immagine,
            title: c.nome,
            stato_iscrizione: c.stato_iscrizione,
            bg: c.bg, // in case bg is needed
            pay: c.valore_compenso || c.tipo_compenso || '',
            date: c.data?.data_inizio_completa || c.data_completa || '',
            place: c.luogo_completo || ''
          })).sort(() => {
            // random
            return Math.random() - 0.5
          })
        }
      } catch (e) {
        console.error("Error fetching castings:", e)
        this.castings = []
      }
    },
    async fetchRecentCastings() {
      try {
        const res = await appClient.post('/web/casting/list', { tipo_lista: 1 })
        if (res.data && res.data.status && Array.isArray(res.data.data)) {
          this.recentCastings = res.data.data.map(c => ({
            id: c.id,
            codice: c.codice,
            image: c.immagine,
            title: c.nome,
            stato_iscrizione: c.stato_iscrizione,
            bg: c.bg, // in case bg is needed
            pay: c.valore_compenso || c.tipo_compenso || '',
            date: c.data?.data_inizio_completa || c.data_completa || '',
            place: c.luogo_completo || ''
          }))
        }
      } catch (e) {
        console.error("Error fetching recent castings:", e)
        this.recentCastings = []
      }
    },
    checkDesktop() {
      this.isDesktop = window.matchMedia('(min-width: 768px)').matches
    },
    goToCasting(id) {
      if (this.isDesktop) {
        this.$router.push(`/overview/casting/${id}`)
      } else {
        this.$router.push(`/casting/${id}`)
      }
    },
    logout() {
      localStorage.clear();
      location.reload();
    },
    onCloseCastingSheet() {
      this.$router.push('/overview')
    },
    goToProfile() {
      if (!this.profile) {
        // go to overview
        if(this.$route.name !== 'Overview'){
          this.$router.push('/overview');
        }
        return;
      }
      this.$router.push({ name: 'Profile' });
    },
    scrollCastings(direction) {
      // Scroll the castings row left or right by 300px
      const row = this.$refs.castingsRow;
      if (row) {
        const amount = 300;
        if (direction === 'left') {
          row.scrollBy({ left: -amount, behavior: 'smooth' });
        } else if (direction === 'right') {
          row.scrollBy({ left: amount, behavior: 'smooth' });
        }
      }
    }
  }
}
</script>
